import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader, TicketDetailModal, CreateTicketModal } from '../shared';
import { DeliveryIssuesTable, DeliveryDetailCard } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { DeliveryIssueRow, DeliveryDetail } from './callCenterDeliveryIssuesTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus, formatDateTime } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import { formatDeliveryId } from '../../../utils/formatId';
import styles from './CallCenterDeliveryIssues.module.css';

function adapt(t: Ticket): DeliveryIssueRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    deliveryId: t.order_id ? formatDeliveryId(t.order_id) : '—',
    driver: t.assigned_to_name || 'Unassigned',
    failureReason: t.subject,
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
  };
}

function buildDetail(t: Ticket | undefined): DeliveryDetail | null {
  if (!t) return null;
  const timeline = (t.messages ?? []).slice(0, 4).map((m, i) => ({
    id: `tl-${m.id}`,
    title: m.body.slice(0, 80),
    meta: `${formatDateTime(m.created_at)} · ${m.sender_name || 'System'}`,
    dot: (['green', 'blue', 'orange', 'gray'] as const)[i % 4],
  }));
  return {
    deliveryId: t.order_id ? formatDeliveryId(t.order_id) : '—',
    timeline,
    driverNotes: t.description || '—',
  };
}

const CallCenterDeliveryIssues: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const current = useTicketsStore((s) => s.current);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);
  const fetchOne = useTicketsStore((s) => s.fetchOne);

  useEffect(() => {
    setFilters({ category: 'delivery' });
    fetchList();
  }, [setFilters, fetchList]);

  useEffect(() => {
    if (items.length > 0) fetchOne(items[0].id).catch(() => undefined);
  }, [items, fetchOne]);

  const rows = useMemo<DeliveryIssueRow[]>(() => items.map(adapt), [items]);
  const detail = useMemo(() => buildDetail(current ?? undefined), [current]);

  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Logistics problems, failed deliveries, driver incidents"
        onAction={() => setShowCreate(true)}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No delivery issues to display"
        onRetry={() => fetchList()}
      >
        <DeliveryIssuesTable rows={rows} onView={(id) => setOpenTicketId(parseInt(id, 10))} />
        {detail && <DeliveryDetailCard detail={detail} />}
      </AsyncBoundary>
      <TicketDetailModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <CreateTicketModal
        open={showCreate}
        defaultCategory="delivery"
        lockCategory
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setOpenTicketId(id); fetchList(); }}
      />
    </div>
  );
};

export const CallCenterDeliveryIssuesPage: React.FC = () => <CallCenterDeliveryIssues />;

export { CallCenterDeliveryIssues };
export default CallCenterDeliveryIssues;
